// ---------------------------------------------------------------------------
// useStableViewportHeight — iOS-PWA-safe replacement for `100lvh` / `100vh`.
// ---------------------------------------------------------------------------
// THE PROBLEM (REPRODUCED IN PLAYWRIGHT AND VISUAL-VERIFIED)
//   On iOS Safari in PWA-standalone mode with
//   `interactive-widget=resizes-content` (our viewport meta tag), every
//   CSS viewport-height unit COLLAPSES when the on-screen keyboard
//   appears — `vh`, `dvh`, `lvh`, `svh`. The CSS spec says `lvh` is the
//   largest viewport and SHOULD be stable across keyboard show/hide;
//   iOS Safari simply does not implement it that way.
//
//   When `100lvh` collapses, the gradient layer (`.bg-stage__floor`,
//   sized `100vw x 100lvh`) gets squeezed into the visible strip above
//   the keyboard. The user sees:
//     • the green/blue `--bg-3` end of the gradient compressed right
//       under the composer pill ("green block behind the white bar"), and
//     • the html/body solid `--bg-3` background colour exposed wherever
//       the squeezed gradient no longer reaches.
//
// THE FIX
//   This hook installs a single CSS custom property on `<html>`:
//
//       --app-stable-h: <pixels>px
//
//   It is set to the LARGEST `window.innerHeight` value the page has
//   ever observed since the last orientation change. Because the
//   keyboard only ever SHRINKS `innerHeight`, the hook ignores those
//   smaller measurements and the value stays glued to the keyboard-DOWN
//   reading — i.e. the true full-screen height. Orientation change wipes
//   the cap and re-measures so portrait <-> landscape continues to work.
//
//   All gradient-sizing CSS now reads from this variable with `100lvh`
//   as the pre-JS / non-supporting-browser fallback:
//
//       background-size: 100vw var(--app-stable-h, 100lvh);
//       height: calc(var(--app-stable-h, 100lvh) + Npx);
//
// WHAT THIS DOES NOT TOUCH
//   Composer position, keyboard logic, `--kb-offset`, `--composer-bottom-pad`,
//   scroll-lock, day/night theme, sparkle canvases. The hook only changes
//   how tall the gradient image draws. Real users on real desktop / non-iOS
//   browsers never see any visible difference (their viewports don't
//   shrink for the keyboard anyway).
// ---------------------------------------------------------------------------
import { useEffect } from 'react';

const CSS_VAR = '--app-stable-h';

export function useStableViewportHeight() {
  useEffect(() => {
    if (typeof window === 'undefined') return undefined;

    let maxSeen = 0;

    const apply = (px) => {
      document.documentElement.style.setProperty(CSS_VAR, `${px}px`);
    };

    const measure = () => {
      const h = window.innerHeight;
      if (h > maxSeen) {
        maxSeen = h;
        apply(h);
      }
    };

    const reset = () => {
      // Portrait <-> landscape changes the true full-screen height —
      // wipe the cap and re-measure once the rotation animation has
      // settled. 300 ms is comfortably longer than iOS's ~250 ms
      // rotation animation.
      maxSeen = 0;
      window.setTimeout(measure, 300);
    };

    measure();
    window.addEventListener('resize', measure);
    window.addEventListener('orientationchange', reset);

    return () => {
      window.removeEventListener('resize', measure);
      window.removeEventListener('orientationchange', reset);
    };
  }, []);
}
